/**
 * The starting shrine of a map (ADR-096's amendment of 2026-10-05): the one shrine every character knows from the
 * start, without walking into its ring. It is in every character's set by rule, so nothing is stored for it, it grants
 * no experience and tells no discovery. It is the shrine whose `start` field its builder ticked, and a map on which
 * none is ticked has none: its characters start at the Spawn Point and know no shrine. The map server reads the mark
 * alone (it passes no Spawn Point). The nearest shrine to a Spawn Point is the editor's upgrade's, for a map saved
 * before the field existed.
 */

/**
 * The field of a shrine, as a thing and as a marker, that marks it the starting one.
 */
export const SHRINE_START_FIELD = "start";

/**
 * A shrine as the rule reads it: where it stands, in any one unit, and whether it is marked.
 */
export interface StartCandidate {
  readonly x: number;
  readonly y: number;
  readonly start: boolean;
}

/**
 * Whether a shrine's fields mark it the starting one.
 *
 * @param fields The shrine's placed fields.
 * @returns True only for the boolean `start: true`.
 */
export function isStartingShrine(fields: Readonly<Record<string, unknown>> | undefined): boolean {
  return fields?.[SHRINE_START_FIELD] === true;
}

/**
 * The starting shrine among a map's shrines: the first marked one; with none marked, the one nearest the Spawn Point
 * by straight line, the lower `y`, then the lower `x`, then the earlier in the list where two are as near.
 *
 * @param shrines The map's shrines, in an order the caller keeps stable.
 * @param spawn The map's Spawn Point in the shrines' unit, or null for a map without one.
 * @returns The shrine's place in the list, or -1 when the map has no shrine, or none marked and no Spawn Point.
 * @example
 * startingShrine([{ x: 400, y: 0, start: false }, { x: 100, y: 100, start: false }], { x: 0, y: 0 });
 * // 1
 */
export function startingShrine(shrines: readonly StartCandidate[], spawn: { readonly x: number; readonly y: number } | null): number {
  const marked = shrines.findIndex((shrine) => shrine.start);
  if (marked >= 0 || spawn === null) {
    return marked;
  }
  let best = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < shrines.length; index += 1) {
    const shrine = shrines[index] as StartCandidate;
    const distance = (shrine.x - spawn.x) * (shrine.x - spawn.x) + (shrine.y - spawn.y) * (shrine.y - spawn.y);
    const held = shrines[best];
    if (Number.isFinite(distance) && (distance < bestDistance || (distance === bestDistance && held !== undefined && (shrine.y < held.y || (shrine.y === held.y && shrine.x < held.x))))) {
      best = index;
      bestDistance = distance;
    }
  }
  return best;
}
